import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Chart, ChartConfiguration, registerables, type TooltipItem } from 'chart.js';
import { BubbleMapController, GeoFeature, ProjectionScale, SizeScale, topojson, type IBubbleMapDataPoint } from 'chartjs-chart-geo';
import type { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import type { GeometryCollection, Topology } from 'topojson-specification';
import { CardModule } from 'primeng/card';

import { LayoutService } from '@/layout/service/layout.service';
import { MedicoAtivoService } from './medico-ativo.service';

Chart.register(...registerables, BubbleMapController, GeoFeature, ProjectionScale, SizeScale);

type GeoJsonPropertiesWithName = GeoJsonProperties & { name?: string };

type GeoJsonFeatureCollection = FeatureCollection<Geometry, GeoJsonPropertiesWithName>;

type BrasilTopology = Topology<{ bra: GeometryCollection<GeoJsonPropertiesWithName> }>;

type CorRgb = {
    r: number;
    g: number;
    b: number;
};

type EstadoCoordenada = {
    uf: string;
    nome: string;
    latitude: number;
    longitude: number;
};

type MedicoBubblePoint = IBubbleMapDataPoint & {
    uf: string;
    nome: string;
};

type LegendaMedicoUf = {
    uf: string;
    nome: string;
    total: number;
};

type CoresBolha = {
    preenchimento: string;
    preenchimentoHover: string;
    borda: string;
    bordaMapa: string;
};

@Component({
    selector: 'app-mapa-calor',
    standalone: true,
    imports: [CommonModule, CardModule],
    templateUrl: './mapa-calor.html',
    styleUrl: './mapa-calor.scss'
})
export class MapaCalor implements AfterViewInit, OnDestroy, OnInit {
    @ViewChild('mapaCalorCanvas', { static: true })
    private canvasRef!: ElementRef<HTMLCanvasElement>;

    private chart: Chart<'bubbleMap', MedicoBubblePoint[]> | null = null;
    private abortController: AbortController | null = null;
    private corPrimariaRgb: CorRgb | null = null;
    private deveAtualizarCores = false;
    private totaisPorUf = new Map<string, number>();
    private pontosMedicos: MedicoBubblePoint[] = [];
    legendaMedicos: LegendaMedicoUf[] = [];
    private readonly estados: EstadoCoordenada[] = [
        { uf: 'AC', nome: 'Acre', latitude: -9.9754, longitude: -67.8243 },
        { uf: 'AL', nome: 'Alagoas', latitude: -9.6498, longitude: -35.7089 },
        { uf: 'AP', nome: 'Amapá', latitude: 0.0349, longitude: -51.0694 },
        { uf: 'AM', nome: 'Amazonas', latitude: -3.119, longitude: -60.0217 },
        { uf: 'BA', nome: 'Bahia', latitude: -12.9777, longitude: -38.5016 },
        { uf: 'CE', nome: 'Ceará', latitude: -3.7319, longitude: -38.5267 },
        { uf: 'DF', nome: 'Distrito Federal', latitude: -15.7939, longitude: -47.8828 },
        { uf: 'ES', nome: 'Espírito Santo', latitude: -20.3155, longitude: -40.3128 },
        { uf: 'GO', nome: 'Goiás', latitude: -16.6869, longitude: -49.2648 },
        { uf: 'MA', nome: 'Maranhão', latitude: -2.5307, longitude: -44.3068 },
        { uf: 'MT', nome: 'Mato Grosso', latitude: -15.601, longitude: -56.0974 },
        { uf: 'MS', nome: 'Mato Grosso do Sul', latitude: -20.4697, longitude: -54.6201 },
        { uf: 'MG', nome: 'Minas Gerais', latitude: -19.9245, longitude: -43.9352 },
        { uf: 'PA', nome: 'Pará', latitude: -1.4558, longitude: -48.5024 },
        { uf: 'PB', nome: 'Paraíba', latitude: -7.1195, longitude: -34.845 },
        { uf: 'PR', nome: 'Paraná', latitude: -25.4284, longitude: -49.2733 },
        { uf: 'PE', nome: 'Pernambuco', latitude: -8.0476, longitude: -34.877 },
        { uf: 'PI', nome: 'Piauí', latitude: -5.0919, longitude: -42.8034 },
        { uf: 'RJ', nome: 'Rio de Janeiro', latitude: -22.9068, longitude: -43.1729 },
        { uf: 'RN', nome: 'Rio Grande do Norte', latitude: -5.7793, longitude: -35.2009 },
        { uf: 'RS', nome: 'Rio Grande do Sul', latitude: -30.0346, longitude: -51.2177 },
        { uf: 'RO', nome: 'Rondônia', latitude: -8.7608, longitude: -63.8999 },
        { uf: 'RR', nome: 'Roraima', latitude: 2.8235, longitude: -60.6753 },
        { uf: 'SC', nome: 'Santa Catarina', latitude: -27.5954, longitude: -48.548 },
        { uf: 'SP', nome: 'São Paulo', latitude: -23.5505, longitude: -46.6333 },
        { uf: 'SE', nome: 'Sergipe', latitude: -10.9092, longitude: -37.0677 },
        { uf: 'TO', nome: 'Tocantins', latitude: -10.1841, longitude: -48.3336 }
    ];

    constructor(
        private layoutService: LayoutService,
        private medicoAtivoService: MedicoAtivoService,
        private destroyRef: DestroyRef,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.layoutService.configUpdate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.atualizarCores();
        });

        this.carregarTotaisMedicosAtivos();
    }

    ngAfterViewInit(): void {
        void this.carregarMapa();
    }

    ngOnDestroy(): void {
        this.abortController?.abort();
        this.chart?.destroy();
    }

    private async carregarMapa(): Promise<void> {
        if (!this.canvasRef?.nativeElement) {
            return;
        }

        this.abortController?.abort();
        this.abortController = new AbortController();

        let geoJson: GeoJsonFeatureCollection;

        try {
            const response = await fetch('geo/bra.topo.json', { signal: this.abortController.signal });

            if (!response.ok) {
                return;
            }

            const brasil = (await response.json()) as BrasilTopology;
            geoJson = topojson.feature(brasil, brasil.objects.bra) as GeoJsonFeatureCollection;
        } catch (erro) {
            if ((erro as DOMException)?.name === 'AbortError') {
                return;
            }

            console.error('Falha ao carregar o mapa de calor do Brasil.', erro);
            return;
        }
        const pontos = this.pontosMedicos.length > 0 ? this.pontosMedicos : this.criarPontosMedicos(this.totaisPorUf);
        this.pontosMedicos = pontos;
        const cores = this.criarCoresBolha(this.obterCorPrimariaRgb());
        const escalas = {
            projection: {
                axis: 'x',
                type: 'projection',
                position: 'chartArea',
                projection: 'mercator',
                display: false
            },
            size: {
                axis: 'x',
                type: 'size',
                display: false,
                range: [4, 26]
            }
        } as Record<string, Record<string, unknown>>;

        const config: ChartConfiguration<'bubbleMap', MedicoBubblePoint[]> = {
            type: 'bubbleMap',
            data: {
                labels: pontos.map((ponto) => `${ponto.nome} (${ponto.uf})`),
                datasets: [
                    {
                        label: 'Medicos',
                        outline: geoJson.features ?? [],
                        showOutline: true,
                        data: pontos,
                        backgroundColor: cores.preenchimento,
                        borderColor: cores.borda,
                        borderWidth: 1,
                        hoverBackgroundColor: cores.preenchimentoHover,
                        hoverBorderColor: cores.borda,
                        outlineBorderColor: cores.bordaMapa,
                        outlineBorderWidth: 0.6
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: {
                    mode: 'nearest',
                    intersect: true
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            title: (items) => {
                                const ponto = items[0]?.raw as MedicoBubblePoint | undefined;
                                if (!ponto) {
                                    return items[0]?.label ?? '';
                                }
                                return `${ponto.nome} (${ponto.uf})`;
                            },
                            label: (item: TooltipItem<'bubbleMap'>) => {
                                const ponto = item.raw as MedicoBubblePoint | undefined;
                                const valor = ponto?.value ?? 0;
                                return `Medicos ativos: ${valor.toLocaleString('pt-BR')}`;
                            }
                        }
                    }
                },
                scales: escalas as NonNullable<ChartConfiguration<'bubbleMap'>['options']>['scales']
            }
        };

        this.chart?.destroy();
        this.chart = new Chart(this.canvasRef.nativeElement, config);

        if (this.deveAtualizarCores) {
            this.deveAtualizarCores = false;
            this.atualizarCores();
        }
    }

    private criarPontosMedicos(totaisPorUf: Map<string, number>): MedicoBubblePoint[] {
        return this.estados.map((estado) => ({
            latitude: estado.latitude,
            longitude: estado.longitude,
            value: this.obterTotalPorUf(estado.uf, totaisPorUf),
            uf: estado.uf,
            nome: estado.nome
        }));
    }

    private obterTotalPorUf(uf: string, totaisPorUf: Map<string, number>): number {
        const total = totaisPorUf.get(uf);
        if (total === undefined || total === null) {
            return 0;
        }

        return Math.max(total, 0);
    }

    private carregarTotaisMedicosAtivos(): void {
        this.medicoAtivoService
            .consultarTotalMedicosAtivosPorUf()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (totais) => {
                    this.totaisPorUf = this.normalizarTotaisPorUf(totais);
                    this.atualizarPontosMapa();
                },
                error: () => {
                    this.totaisPorUf = new Map();
                    this.atualizarPontosMapa();
                }
            });
    }

    private normalizarTotaisPorUf(totais: { ufSgUf: string; total: number }[]): Map<string, number> {
        const mapa = new Map<string, number>();

        totais.forEach((item) => {
            const uf = item.ufSgUf?.trim().toUpperCase();
            if (!uf) {
                return;
            }

            mapa.set(uf, item.total ?? 0);
        });

        return mapa;
    }

    private atualizarPontosMapa(): void {
        const pontos = this.criarPontosMedicos(this.totaisPorUf);
        this.pontosMedicos = pontos;
        this.legendaMedicos = this.criarLegenda(this.totaisPorUf);
        // markForCheck: a legenda vem de callback assíncrono e é gravada em campo não-signal; sob layout OnPush não renderizaria sem marcar o caminho como dirty.
        this.cdr.markForCheck();

        if (!this.chart) {
            return;
        }

        this.chart.data.labels = pontos.map((ponto) => `${ponto.nome} (${ponto.uf})`);
        this.chart.data.datasets[0].data = pontos;
        this.chart.update();
    }

    private criarLegenda(totaisPorUf: Map<string, number>): LegendaMedicoUf[] {
        const legenda = this.estados.map((estado) => ({
            uf: estado.uf,
            nome: estado.nome,
            total: this.obterTotalPorUf(estado.uf, totaisPorUf)
        }));

        // O cabecalho da legenda anuncia "Ordenado por maior total": ordena por total, UF como desempate.
        return legenda.sort((a, b) => (b.total ?? 0) - (a.total ?? 0) || a.uf.localeCompare(b.uf, 'pt-BR'));
    }

    private atualizarCores(): void {
        if (!this.chart) {
            this.deveAtualizarCores = true;
            return;
        }

        requestAnimationFrame(() => {
            if (!this.chart) {
                return;
            }

            this.corPrimariaRgb = null;
            const cores = this.criarCoresBolha(this.obterCorPrimariaRgb());
            const dataset = this.chart.data.datasets[0];

            dataset.backgroundColor = cores.preenchimento;
            dataset.borderColor = cores.borda;
            dataset.hoverBackgroundColor = cores.preenchimentoHover;
            dataset.hoverBorderColor = cores.borda;
            (dataset as { outlineBorderColor?: string }).outlineBorderColor = cores.bordaMapa;
            this.chart.update();
        });
    }

    private criarCoresBolha(corBase: CorRgb): CoresBolha {
        const corClara = this.misturarCores(corBase, { r: 255, g: 255, b: 255 }, 0.3);
        const corEscura = this.misturarCores(corBase, { r: 0, g: 0, b: 0 }, 0.2);

        return {
            preenchimento: `rgba(${corClara.r}, ${corClara.g}, ${corClara.b}, 0.55)`,
            preenchimentoHover: `rgba(${corBase.r}, ${corBase.g}, ${corBase.b}, 0.75)`,
            borda: `rgb(${corEscura.r}, ${corEscura.g}, ${corEscura.b})`,
            bordaMapa: `rgba(${corEscura.r}, ${corEscura.g}, ${corEscura.b}, 0.35)`
        };
    }

    private obterCorPrimariaRgb(): CorRgb {
        if (this.corPrimariaRgb) {
            return this.corPrimariaRgb;
        }

        const estilos = getComputedStyle(document.documentElement);
        const corPrimaria = estilos.getPropertyValue('--p-primary-color').trim() || estilos.getPropertyValue('--primary-color').trim() || '#3b82f6';

        this.corPrimariaRgb = this.parsearCorParaRgb(corPrimaria);
        return this.corPrimariaRgb;
    }

    private misturarCores(corA: CorRgb, corB: CorRgb, fator: number): CorRgb {
        const t = Math.min(Math.max(fator, 0), 1);

        return {
            r: Math.round(corA.r + (corB.r - corA.r) * t),
            g: Math.round(corA.g + (corB.g - corA.g) * t),
            b: Math.round(corA.b + (corB.b - corA.b) * t)
        };
    }

    private parsearCorParaRgb(cor: string): CorRgb {
        const valor = cor.trim();

        if (valor.startsWith('#')) {
            const hex = valor.slice(1);

            if (hex.length === 3) {
                const r = parseInt(hex[0] + hex[0], 16);
                const g = parseInt(hex[1] + hex[1], 16);
                const b = parseInt(hex[2] + hex[2], 16);

                return { r, g, b };
            }
            if (hex.length === 6) {
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);

                return { r, g, b };
            }
        }

        const match = valor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);

        if (match) {
            return {
                r: Number.parseInt(match[1], 10),
                g: Number.parseInt(match[2], 10),
                b: Number.parseInt(match[3], 10)
            };
        }

        return { r: 59, g: 130, b: 246 };
    }
}
